import { Exclamation } from "@repo/icons";
import { useRouter, useSearch } from "@tanstack/react-router";
import { CpsAlert } from "corpus";
import { graphql } from "@repo/graphql-types/gql";
import { PatientsPaymentsQueryDocument } from "@repo/graphql-types/graphql";
import {
  MessageDrawerActionButton,
  MessageDrawerActions,
  MessageDrawerBody,
  MessageDrawerRoot,
  MessageDrawerTitle,
} from "@/components/message-drawer";
import {
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
} from "@/hooks/use-graphql";
import { UnexpectedErrorDrawer } from "@/components/unexpected-error-drawer";
import { GenericSuccessDrawer } from "@/components/generic-success-drawer";

const ChangeTotemPaymentStatusMutation = graphql(`
  mutation CancelTotemPayment($codAgendamento: Int!) {
    ExpensumCancelTotemPayment(arg1: { codAgendamento: $codAgendamento }) {
      codAgendamento
    }
  }
`);

interface SearchParams {
  action?: string;
  guid?: string;
  appointmentId?: string;
}

export const PaymentStatusChangeDrawer = (): JSX.Element => {
  const { action, guid, appointmentId }: SearchParams = useSearch({
    strict: false,
  });

  const router = useRouter();

  const invalidatePatientsPaymentsQuery = useInvalidateQuery(
    PatientsPaymentsQueryDocument,
  );

  const { mutateAsync } = useGraphQLMutationWithErrorHandler(
    ChangeTotemPaymentStatusMutation,
  );

  const isOpen = action === "payment-status-change";

  const handleSuccessDrawerDismiss = (value: boolean): void => {
    if (!value && action === "success") {
      invalidatePatientsPaymentsQuery();
      router.history.go(-2);
    }
  };

  const handleErrorDrawerDismiss = (value: boolean): void => {
    if (!value && action === "error") {
      router.history.back();
    }
  };

  const handleDrawerDismiss = (value: boolean): void => {
    if (!value && isOpen) {
      router.history.back();
    }
  };

  const handlePaymentStatusChange = async (): Promise<void> => {
    if (!guid && appointmentId) {
      await submitTotemPaymentStatusChange();
      return;
    }
    void router.navigate({
      to: "/settings/payments/patient-payments/$guid/change-online-payment-status",
      params: { guid: guid ?? "" },
      replace: true,
    });
  };

  const submitTotemPaymentStatusChange = async (): Promise<void> => {
    const onSuccess = (): void => {
      void router.navigate({
        search: { appointmentId, action: "success" },
        replace: true,
      });
    };
    const onError = (): void => {
      void router.navigate({ search: { appointmentId, action: "error" }, replace: true });
    };

    try {
      await mutateAsync(
        { codAgendamento: Number(appointmentId) },
        { onSuccess, onError },
      );
    } catch (error) {
      onError();
    }
  };

  const mainParagraphText = guid ? (
    <p data-testid="payment-status-change-drawer-main-paragraph">
      Ao confirmar, o link será invalidado e o status do pagamento atualizado para
      <p className="font-semibold">&ldquo;Cancelado&ldquo;.</p> Esta ação
      <span className="font-semibold">&nbsp;não pode ser desfeita.</span>
    </p>
  ) : (
    <p data-testid="payment-status-change-drawer-main-paragraph">
      Ao confirmar, o envio ao totem será cancelado e o mesmo será{" "}
      <span className="font-semibold">removido</span> do relatório. Esta ação{" "}
      <span className="font-semibold">não pode ser desfeita.</span>
    </p>
  );

  return (
    <>
      <MessageDrawerRoot open={isOpen} setOpen={handleDrawerDismiss}>
        <MessageDrawerBody className="max-h-[80vh]">
          <div className="bg-danger-50 rounded-full w-[72px] h-[72px] aspect-square flex justify-center items-center self-center mb-6">
            <Exclamation size={40} className="fill-danger-400" />
          </div>
          <MessageDrawerTitle>Deseja cancelar este pagamento?</MessageDrawerTitle>
          {mainParagraphText}
          <div className="flex flex-row justify-between gap-2 mt-4">
            <CpsAlert
              title="Se o paciente já estiver finalizando o pagamento, a cobrança ainda poderá ser concluída. Oriente seu paciente sobre o cancelamento."
              type="info"
            />
          </div>
          <MessageDrawerActions>
            <MessageDrawerActionButton
              onClick={() => {
                void handlePaymentStatusChange();
              }}
            >
              Cancelar
            </MessageDrawerActionButton>
            <MessageDrawerActionButton
              secondary
              onClick={() => handleDrawerDismiss(false)}
            >
              Manter pagamento ativo
            </MessageDrawerActionButton>
          </MessageDrawerActions>
        </MessageDrawerBody>
      </MessageDrawerRoot>

      <GenericSuccessDrawer
        open={action === "success"}
        title="Status do pagamento alterado!"
        setOpen={handleSuccessDrawerDismiss}
        confirmButtonText="Voltar"
      />
      <UnexpectedErrorDrawer
        open={action === "error"}
        setOpen={handleErrorDrawerDismiss}
      />
    </>
  );
};
